import React from 'react';
import { BankSearch } from '../../../../utils/types/candi_bank_types';
import {
  formatRelativeTime,
  sourceLabel,
  sourceBadgeClasses,
  statusPillClasses,
} from '../../utils/bankSearchAdapters';

interface BankSearchListProps {
  searches: BankSearch[];
  total: number;
  page: number;
  loading: boolean;
  activeSearchId: string | null;
  onSelect: (search: BankSearch) => void;
  onDelete: (searchId: string) => void;
  onPageChange: (page: number) => void;
}

export const BankSearchList: React.FC<BankSearchListProps> = ({
  searches,
  total,
  page,
  loading,
  activeSearchId,
  onSelect,
  onDelete,
  onPageChange,
}) => {
  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this saved search?')) {
      onDelete(id);
    }
  };

  const totalPages = Math.ceil(total / 20);

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse flex flex-col p-3 rounded-lg border border-slate-100 bg-slate-50 gap-2">
              <div className="h-4 bg-slate-200 rounded w-3/4"></div>
              <div className="flex gap-2">
                <div className="h-3 bg-slate-200 rounded w-16"></div>
                <div className="h-3 bg-slate-200 rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden min-h-[500px]">
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {searches.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <svg className="w-8 h-8 mb-2 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
              <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
            </svg>
            <p className="text-xs font-medium">No saved searches yet</p>
          </div>
        ) : (
          searches.map((search) => {
            const isActive = search.id === activeSearchId;
            return (
              <div
                key={search.id}
                onClick={() => onSelect(search)}
                className={`group relative flex flex-col p-3 rounded-lg border cursor-pointer transition-all ${
                  isActive
                    ? 'bg-indigo-50 border-indigo-300 shadow-xs'
                    : 'bg-white border-slate-100 hover:bg-slate-50 hover:border-slate-200'
                }`}
              >
                <div className="flex justify-between items-start mb-1.5">
                  <h3 className={`text-xs font-bold truncate pr-6 ${isActive ? 'text-indigo-900' : 'text-slate-800'}`}>
                    {search.title || 'Untitled Search'}
                  </h3>
                  <button
                    onClick={(e) => handleDelete(e, search.id)}
                    className="absolute top-2.5 right-2 p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                    title="Delete Search"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center gap-1.5 text-[10px]">
                  <span className={`px-1.5 py-0.5 rounded font-semibold border ${sourceBadgeClasses(search.source)}`}>
                    {sourceLabel(search.source)}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded font-semibold border ${statusPillClasses(search.status)}`}>
                    {search.status}
                  </span>
                  <span className="text-slate-400 ml-auto whitespace-nowrap font-medium text-[10px]">
                    {formatRelativeTime(search.updatedAt)}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {total > 0 && (
        <div className="p-2.5 border-t border-slate-100 flex items-center justify-between bg-slate-50/80">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="p-1 text-slate-500 hover:text-slate-800 disabled:opacity-30 disabled:hover:text-slate-500 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span className="text-[11px] font-bold text-slate-600">
            Page {page} {totalPages > 0 && `of ${totalPages}`}
          </span>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="p-1 text-slate-500 hover:text-slate-800 disabled:opacity-30 disabled:hover:text-slate-500 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
